import type { RiftboundCard } from '../types';

/** Canonical card-type labels used in filters and on the printed type line. */
export const KNOWN_CARD_TYPES = [
  'Battlefield',
  'Gear',
  'Legend',
  'Other',
  'Rune',
  'Spell',
  'Token',
  'Unit',
] as const;

const KNOWN_TYPE_SET = new Set(KNOWN_CARD_TYPES.map((t) => t.toLowerCase()));

function titleCaseType(value: string): string {
  const s = value.trim();
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Split a type string that may contain multiple known types ("Unit Gear"). */
export function parseCardTypeTokens(raw: string | undefined | null): string[] {
  if (!raw?.trim()) return [];
  const parts = raw
    .trim()
    .split(/[\s/,|]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const out: string[] = [];
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (!KNOWN_TYPE_SET.has(lower)) continue;
    const label = titleCaseType(part);
    if (!out.some((t) => t.toLowerCase() === label.toLowerCase())) out.push(label);
  }
  // Fallback: whole string is a single unknown type
  if (!out.length && raw.trim()) out.push(raw.trim());
  return out;
}

/**
 * Effective type list for a card.
 * Prefers explicit `cardTypes`; otherwise parses `cardType`.
 * Does not include Token from supertype — Token is a printed prefix, not a second type.
 */
export function cardTypeList(
  card: Pick<RiftboundCard, 'cardType' | 'cardTypes'> | { cardType?: string; cardTypes?: string[] },
): string[] {
  if (Array.isArray(card.cardTypes) && card.cardTypes.length) {
    return card.cardTypes.map(titleCaseType).filter(Boolean);
  }
  return parseCardTypeTokens(card.cardType);
}

export function isTokenSupertype(card: { supertype?: string | null }): boolean {
  return (card.supertype ?? '').trim().toLowerCase() === 'token';
}

/**
 * Printed type-line label: "Token Unit", "Token Gear", "Unit Gear", "Champion Unit", …
 */
export function cardTypeLineLabel(
  card: {
    cardType?: string;
    cardTypes?: string[];
    supertype?: string | null;
    name?: string;
  },
): string {
  const types = cardTypeList(card);
  const typePart = types.join(' ');
  if (isTokenSupertype(card) && typePart) return `Token ${typePart}`;
  if (isTokenSupertype(card) && !typePart) return 'Token';
  // Champion Unit is handled by callers that have champion helpers; keep types here.
  return typePart || (card.cardType ?? '').trim();
}

/**
 * Card-type filter match (any-of).
 * Selecting "Token" also matches Token-supertype cards (Sprite, Gold, Recruit…).
 * Selecting "Unit" / "Gear" matches multi-type cards that include that type.
 */
export function cardMatchesTypeFilter(
  card: {
    cardType?: string;
    cardTypes?: string[];
    supertype?: string | null;
  },
  selectedTypes: string[],
): boolean {
  if (!selectedTypes?.length) return true;
  const selected = selectedTypes.map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (!selected.length) return true;

  const types = cardTypeList(card).map((t) => t.toLowerCase());
  const wantsToken = selected.includes('token');
  const otherSelected = selected.filter((t) => t !== 'token');

  if (wantsToken && (isTokenSupertype(card) || types.includes('token'))) return true;
  if (otherSelected.some((t) => types.includes(t))) return true;
  return false;
}
